import axios, {
  type InternalAxiosRequestConfig,
  type AxiosError,
  AxiosHeaders,
} from "axios";
import toast from "react-hot-toast";
import { LStorage } from "../config/constants";
import { signOut } from "store/slicers/auth";
import { EBaseUrl } from "index";
import { type IAuthorizeResponse } from "store/interfaces/auth";
import { isAxiosResponse } from "shared/helpers/isAxiosResponse";
import { clearAuthData, setAuthData } from "shared/helpers/authData";

interface IRefreshResponse {
  data: IAuthorizeResponse;
  success: boolean;
  message: string;
}

type TFailedRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

export const api = axios.create({
  responseType: "json",
});

let store;

export const injectStore = (_store) => {
  store = _store;
};

const feedbackExceptionUrl = "/Feedback/Status/";
const questionExceptionUrl = "/Question";
const feedbackDetailHistoryExceptionUrl = "/Feedback/Logs/";
const customerExceptionUrl = "/Customer";
const dashboardDataUrl = "Dashboard/Data";

api.interceptors.request.use(async (config: any) => {
  const accessToken = localStorage.getItem(LStorage.AUTH) || "";

  config.headers = {
    "Content-Type": "application/json",
    ...config.headers,
    // languageId: localStorage.getItem(LStorage.LANG) || "",
    languageId: "2",
    Authorization: accessToken,
  };

  return config;
});

const refreshApi = axios.create({
  responseType: "json",
});

let refreshPromise: Promise<IRefreshResponse | null> | null = null;

async function refreshAccessToken(): Promise<IRefreshResponse | null> {
  try {
    const refreshToken = localStorage.getItem(LStorage.REFRESH_TOKEN);
    const response = await refreshApi.post<IRefreshResponse>(
      `${EBaseUrl.API}/Refresh`,
      {
        refreshToken,
      }
    );

    return response.data;
  } catch (refreshError) {
    store.dispatch(signOut());
    clearAuthData();
    window.location.href = "/login";
    toast.error("Your session has expired. Please log in to the app");
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as TFailedRequestConfig | undefined;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const status = error.response?.status;

    const isUnauthorized = status === 401;
    const isAlreadyRetried = originalRequest._retry;
    const isRefreshRequest = originalRequest.url?.includes("/auth/refresh");

    if (!isUnauthorized || isAlreadyRetried || isRefreshRequest) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }

    const response = await refreshPromise;
    const newAccessToken = response.data.accessToken;

    if (!newAccessToken) {
      return Promise.reject(error);
    }

    setAuthData(response.data);

    originalRequest.headers = AxiosHeaders.from(originalRequest.headers);
    originalRequest.headers.set("Authorization", newAccessToken);

    return api(originalRequest);
  }
);

api.interceptors.response.use(
  (response) => (isAxiosResponse(response) ? response.data : response),
  (err) => {
    const msg = err?.response?.data?.error?.message?.slice(0, 100);

    return new Promise(async (_, reject) => {
      const t =
        store.getState()?.translations?.translationDataByLangId?.translations;
      if (
        err?.response?.config?.url.includes(feedbackExceptionUrl) ||
        err?.response?.config?.url.includes(questionExceptionUrl) ||
        err?.response?.config?.url.includes(
          feedbackDetailHistoryExceptionUrl
        ) ||
        err?.response?.config?.url.includes(customerExceptionUrl)
      ) {
        return reject(err);
      }
      if (err?.response?.config?.url.includes(dashboardDataUrl)) {
        toast.error(msg ? `${msg}` : t?.["statistical_data_not_found"]);
        return reject(err);
      }
      if (msg) {
        toast.error(t?.[`${msg}`] || `${msg}`);
        reject(err);
      } else {
        toast.error("SMT Went Wrong");
        reject(err);
      }
    });
  }
);
